import { Badge } from "react-bootstrap";
import { getRelativeTime } from "../../../../app/dateHelpers";
import { Icon, IconDefinitions } from "../../../../components";
import { UploadSessionModel } from "../../../../data/entities";
import { getDurationLabel, getFileName } from "../../helper";
import { pillBadge } from "./styles";
import { AlertLevel } from "../../helper";
import { UserService } from "../../../../services";
import { Roles } from "../../../../models";

interface Props {
  session: UploadSessionModel;
  status: AlertLevel;
  onViewResults?: (session: UploadSessionModel) => void;
}

const STATUS_CONFIG: Record<AlertLevel, {
  icon: string;
  iconClass: string;
  badgeBg: string;
  label: string;
}> = {
  Passed: {
    icon: IconDefinitions.circleCheck,
    iconClass: 'text-success',
    badgeBg: 'success',
    label: 'Passed',
  },
  Warning: {
    icon: IconDefinitions.triangleExclamation,
    iconClass: 'text-warning',
    badgeBg: 'warning',
    label: 'Warning',
  },
  Alert: {
    icon: IconDefinitions.triangleExclamation,
    iconClass: 'text-danger',
    badgeBg: 'danger',
    label: 'Alert',
  },
  Error: {
    icon: IconDefinitions.triangleExclamation,
    iconClass: 'text-danger',
    badgeBg: 'danger',
    label: 'Error',
  },
};

export function UploadResultCard({ session, status, onViewResults }: Props) {
  const fileName = getFileName(session.filePath);
  const isError = status === 'Error';
  const config = STATUS_CONFIG[status];
  const isStaff = UserService.isInRole(Roles.admin) || UserService.isInRole(Roles.staff) || UserService.isInRole(Roles.dataAnalyst);

  return (
    <div
      className="card border h-100"
      style={{ transition: 'opacity 0.2s ease' }}
    >
      <div className="card-body p-3">
        {/* Header */}
        <div className="d-flex align-items-start justify-content-between mb-2">
          <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
            <Icon iconName={config.icon} className={`me-2 ${config.iconClass} flex-shrink-0`} />
            <span className="fw-semibold text-truncate">{fileName}</span>
          </div>
          <Badge bg={config.badgeBg} style={pillBadge}>
            {config.label}
          </Badge>
        </div>

        {/* Inspection info */}
        {session.inspectionId && (
          <div className="small text-muted mb-2">
            <Icon iconName={IconDefinitions.clipboard} className="me-1" />
            {session.inspection.reference}
          </div>
        )}

        {/* Error message */}
        {isError && session.errorMessage && (
          <div
            className="rounded-2 p-2 mb-2"
            style={{ backgroundColor: '#fef2f2' }}
          >
            <code
              className="d-block"
              style={{
                fontSize: '0.79rem',
                fontWeight: 500,
                lineHeight: 1.55,
                color: '#b91c1c',
                wordBreak: 'break-word',
              }}
            >
              {session.errorMessage}
            </code>
          </div>
        )}

        {/* Stats row */}
        {!isError && (
          <div className="row g-2 my-2">
            {isStaff && (
              <div className="col-12 col-sm">
                <div className="text-center py-2 px-2 rounded" style={{ backgroundColor: '#f9fafb' }}>
                  <div className="text-muted text-uppercase" style={{ fontSize: '0.65rem', letterSpacing: '0.04em', fontWeight: 500 }}>
                    Total Rows Processed
                  </div>
                  <div className="fw-semibold" style={{ fontSize: '0.82rem', color: '#374151' }}>
                    {session.totalChunks?.toLocaleString()}
                  </div>
                </div>
              </div>
            )}
            <div className="col-12 col-sm">
              <div className="text-center py-2 px-2 rounded" style={{ backgroundColor: '#f9fafb' }}>
                <div className="text-muted text-uppercase" style={{ fontSize: '0.65rem', letterSpacing: '0.04em', fontWeight: 500 }}>
                  Duration
                </div>
                <div className="fw-semibold" style={{ fontSize: '0.82rem', color: '#374151' }}>
                  {getDurationLabel(session.startDate, session.endDate!)}
                </div>
              </div>
            </div>
            <div className="col-12 col-sm">
              <div className="text-center py-2 px-2 rounded" style={{ backgroundColor: '#f9fafb' }}>
                <div className="text-muted text-uppercase" style={{ fontSize: '0.65rem', letterSpacing: '0.04em', fontWeight: 500 }}>
                  Size
                </div>
                <div className="fw-semibold" style={{ fontSize: '0.82rem', color: '#374151' }}>
                  {(session.fileSizeBytes / 1024 / 1024).toFixed(1)} MB
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="d-flex justify-content-between align-items-center mt-2">
          <small className="text-muted" title={new Date(session.endDate!).toLocaleString()}>
            <Icon iconName={IconDefinitions.clock} className="me-1" />
            {isError ? 'Failed' : 'Finished'} {getRelativeTime(new Date(session.endDate!))}
          </small>
          {!isError && onViewResults && (
            <button
              className="btn btn-sm btn-outline-dark"
              onClick={() => onViewResults(session)}
            >
              View Results
              <Icon iconName={IconDefinitions.arrowRight} className="ms-1" />
            </button>
          )}
          {isError && (
            <small className="text-muted">{(session.fileSizeBytes / 1024 / 1024).toFixed(1)} MB</small>
          )}
        </div>
      </div>
    </div>
  );
}